import { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Cloud, Sun, CloudRain } from 'lucide-react';
import { motion } from 'framer-motion';
import { WeatherWidget } from './WeatherWidget';
import { EventDetail } from './EventDetail';
import { AnalyticsDashboard } from './AnalyticsDashboard';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function PlannerPage() {
  const [viewMode, setViewMode] = useState<'calendar' | 'analytics'>('calendar');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date(2026, 2)); // March 2026

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();

  const mockEvents = [
    { date: 5, title: 'Team Meeting', type: 'work' },
    { date: 13, title: 'Date Night', type: 'casual' },
    { date: 18, title: 'Wedding', type: 'formal' },
    { date: 22, title: 'Gym Session', type: 'athletic' },
  ];

  if (viewMode === 'analytics') return <AnalyticsDashboard onBack={() => setViewMode('calendar')} />;
  if (selectedDate) return <EventDetail date={selectedDate} onBack={() => setSelectedDate(null)} />;

  return (
    <div className="flex flex-col h-full bg-[#FAFAF8]">
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Planner</h1>
        <button onClick={() => setViewMode('analytics')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border">Analytics</button>
      </div>

      <WeatherWidget />

      {/* Calendar Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-1"><ChevronLeft size={20} /></button>
        <span className="font-semibold">{MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-1"><ChevronRight size={20} /></button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 px-4">
        {DAYS.map(d => <div key={d} className="text-center text-xs text-gray-400 py-1">{d}</div>)}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 px-4 gap-1 flex-1 content-start pb-24">
        {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const event = mockEvents.find(e => e.date === day);
          const isToday = day === 13;
          return (
            <motion.button key={day} whileTap={{ scale: 0.9 }}
              onClick={() => setSelectedDate(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day))}
              className={`aspect-square rounded-xl flex flex-col items-center justify-center text-sm relative ${isToday ? 'bg-[#6C3CE1] text-white' : 'hover:bg-gray-100'}`}>
              {day}
              {event && <div className={`w-1.5 h-1.5 rounded-full absolute bottom-1 ${isToday ? 'bg-white' : 'bg-[#FF6B5A]'}`} />}
            </motion.button>
          );
        })}
      </div>

      {/* Upcoming Events */}
      <div className="px-4 pb-24">
        <p className="text-xs text-gray-400 font-medium mb-2">UPCOMING</p>
        {mockEvents.map(e => (
          <div key={e.date} className="flex items-center gap-3 bg-white p-3 rounded-xl mb-2">
            <div className="w-10 h-10 rounded-lg bg-[#6C3CE1]/10 flex items-center justify-center text-sm font-bold text-[#6C3CE1]">{e.date}</div>
            <div className="flex-1">
              <p className="text-sm font-medium">{e.title}</p>
              <p className="text-xs text-gray-400 capitalize">{e.type}</p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-gray-100" />
          </div>
        ))}
      </div>
    </div>
  );
}
